import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import router from './router';
import createPersistedState from 'vuex-persistedstate';

let timer = null;
const diff_minutes = (dt2, dt1) => {
  var diff = (dt2.getTime() - dt1.getTime()) / 1000;
  diff /= 60;
  return Math.abs(Math.round(diff));
};

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    accessToken: null,
    loggingIn: false,
    loginError: null,
    userRole: null,
    userName: null,
    timestamp: null,
    permissions: [],
  },
  mutations: {
    loginStart: (state) => (state.loggingIn = true),
    loginStop: (state, errorMessage) => {
      state.loggingIn = false;

      if (errorMessage === 'invalid_credentials') {
        state.loginError = 'The username and/or password are invalid';
      } else if (!errorMessage || errorMessage === 'invalid_grant') {
        state.loginError = 'An unknown error has occured';
      } else {
        state.loginError = errorMessage;
      }
    },
    updateAccessToken: (state, accessToken) => {
      state.accessToken = accessToken;
    },
    updateUserRole: (state, userRole) => {
      state.userRole = userRole;
    },
    updateUserName: (state, userName) => {
      state.userName = userName;
    },
    updatePermissions: (state, permissions) => {
      state.permissions = permissions;
    },
    logout: (state) => {
      state.accessToken = null;
      state.userRole = null;

      localStorage.clear();
    },
    updateAuthTimestamp: (state, timestamp) => {
      state.timestamp = timestamp;
      localStorage.setItem('authTimestamp', timestamp);
    },
  },
  actions: {
    doLogin({ commit, dispatch }, loginData) {
      commit('loginStart');
      localStorage.clear();

      return axios({
        url: process.env.VUE_APP_API_URL + '/oauth/token',
        data: {
          grant_type: 'password',
          username: loginData.email,
          password: loginData.password,
          client_id: process.env.VUE_APP_CLIENT_ID,
          client_secret: process.env.VUE_APP_CLIENT_SECRET,
        },
        method: 'POST',
      })
        .then((response) => {
          const timestamp = new Date();
          localStorage.setItem('accessToken', response.data.access_token);
          localStorage.setItem('user_role', response.data.user_role);
          commit('loginStop', null);
          commit('updateAccessToken', response.data.access_token);
          commit('updateUserRole', response.data.user_role);
          commit('updateUserName', response.data.name);
          commit(
            'updatePermissions',
            response.data.permissions.map((x) => x.name)
          );
          commit('updateAuthTimestamp', timestamp);
          router.push({ name: 'orders' });
        })
        .catch((error) => {
          const message =
            error.response.status === 400
              ? 'The username and/or password are invalid.'
              : error.response.data.error_description;

          commit('loginStop', message);
          commit('updateAccessToken', null);
        });
    },
    fetchAccessToken({ commit, dispatch }) {
      commit('updateAccessToken', localStorage.getItem('accessToken'));
      commit('updateAuthTimestamp', localStorage.getItem('authTimestamp'));
      dispatch('checkActiveSession');
      dispatch('addEventListeners');
    },
    logout({ commit, dispatch }) {
      commit('logout');
      dispatch('removeEventListeners');
      router.push('/login');
      localStorage.clear();
    },
    checkActiveSession({ dispatch, state }) {
      if (timer) clearTimeout(timer);

      timer = setTimeout(() => {
        const now = new Date();
        const end = new Date(state.timestamp);

        const difference = diff_minutes(now, end);

        if (difference > 30) {
          dispatch('logout');
        } else {
          dispatch('checkActiveSession');
        }
      }, 60 * 1000);
    },
    addEventListeners({ commit }) {
      window.addEventListener(
        'mousemove',
        (event) => {
          commit('updateAuthTimestamp', new Date());
        },
        false
      );

      window.addEventListener(
        'keydown',
        (event) => {
          commit('updateAuthTimestamp', new Date());
        },
        false
      );
    },
    removeEventListeners({ commit }) {
      window.removeEventListener(
        'mousemove',
        (event) => {
          commit('updateAuthTimestamp', new Date());
        },
        false
      );

      window.removeEventListener(
        'keydown',
        (event) => {
          commit('updateAuthTimestamp', new Date());
        },
        false
      );
    },
  },
  getters: {
    userRole: (state) => {
      return state.userRole;
    },
    userName: (state) => {
      return state.userName;
    },
    permissions: (state) => {
      return state.permissions;
    },
  },
  plugins: [
    createPersistedState({
      storage: localStorage,
      paths: ['accessToken', 'userRole', 'userName', 'timestamp', 'permissions'],
    }),
  ],
});
